import { request, requestFile } from './http';

export function login(email, password) {
  return request('/auth/login', { method: 'POST', auth: false, body: { email, password } });
}

export function logout(refreshToken) {
  return request('/auth/logout', {
    method: 'POST',
    auth: false,
    body: { refresh_token: refreshToken },
  });
}

export function recoverPassword(email) {
  return request('/auth/password/recover', { method: 'POST', auth: false, body: { email } });
}

export function getMe() {
  return request('/auth/me');
}

export function getProfile() {
  return request('/profile');
}

export function updateProfile(body) {
  return request('/profile', { method: 'PATCH', body });
}

export function updateTheme(theme) {
  return request('/profile/theme', { method: 'PATCH', body: { theme } });
}

export function changePassword(body) {
  return request('/profile/password', { method: 'POST', body });
}

export function requestEmailChange(email) {
  return request('/profile/email', { method: 'POST', body: { email } });
}

export function getProfileMenu() {
  return request('/profile/menu');
}

export function listProfileSessions() {
  return request('/profile/sessions');
}

export function revokeProfileSession(id) {
  return request(`/profile/sessions/${id}/revoke`, { method: 'POST' });
}

export function revokeAllProfileSessions() {
  return request('/profile/sessions/revoke-all', { method: 'POST' });
}

export function getMySections() {
  return request('/access/sections');
}

export function getStartSection() {
  return request('/access/start');
}

export function visitSection(section) {
  return request('/access/visit', { method: 'POST', body: { section } });
}

function userQuery({ page, perPage, order }) {
  const query = { page, sort: 'full_name', order };
  // Общий параметр per_page принимает 20, 50 и 100. 30 — размер по умолчанию,
  // его не передают: явное значение сервер отвечает 422.
  if (perPage !== 30) query.per_page = perPage;
  return query;
}

function userFilters(filters) {
  const body = {};
  if (filters.firstName) body.first_name = filters.firstName;
  if (filters.lastName) body.last_name = filters.lastName;
  if (filters.phone) body.phone = filters.phone;
  if (filters.email) body.email = filters.email;
  if (filters.role) body.role = [filters.role];
  if (filters.deleted) body.deleted = true;
  return body;
}

export function listUsers({ page, perPage, order, filters }) {
  const query = userQuery({ page, perPage, order });
  const body = userFilters(filters);
  if (Object.keys(body).length) {
    return request('/users', { method: 'POST', query, body });
  }
  return request('/users', { query });
}

export function createUser(body) {
  return request('/users/create', { method: 'POST', body });
}

export function generatePassword() {
  return request('/users/generate-password', { method: 'POST' });
}

export function getUser(id) {
  return request(`/users/${id}`);
}

export function updateUser(id, body) {
  return request(`/users/${id}`, { method: 'PATCH', body });
}

export function listManagers({ page, perPage, sort, order, filters } = {}) {
  const query = {};
  if (page) query.page = page;
  if (perPage && perPage !== 30) query.per_page = perPage;
  if (sort) query.sort = sort;
  if (order) query.order = order;
  const body = filters && Object.keys(filters).length ? filters : null;
  if (body) {
    return request('/managers', { method: 'POST', query, body });
  }
  return request('/managers', { query });
}

export function getManagerTableState() {
  return request('/managers/table-state');
}

export function putManagerTableState(body) {
  return request('/managers/table-state', { method: 'PUT', body });
}

export function listManagerViewPresets() {
  return request('/managers/view-presets');
}

export function createManagerViewPreset(body) {
  return request('/managers/view-presets', { method: 'POST', body });
}

export function updateManagerViewPreset(id, body) {
  return request(`/managers/view-presets/${id}`, { method: 'PUT', body });
}

export function deleteManagerViewPreset(id) {
  return request(`/managers/view-presets/${id}`, { method: 'DELETE' });
}

export function listDivisions({ sort, order } = {}) {
  const query = {};
  if (sort) query.sort = sort;
  if (order) query.order = order;
  return request('/managers/divisions', { query });
}

export function createDivision(body) {
  return request('/managers/divisions', { method: 'POST', body });
}

export function getDivision(id) {
  return request(`/managers/divisions/${id}`);
}

export function updateDivision(id, body) {
  return request(`/managers/divisions/${id}`, { method: 'PATCH', body });
}

export function deleteDivision(id, comment) {
  const body = comment ? { comment } : {};
  return request(`/managers/divisions/${id}`, { method: 'DELETE', body });
}

export function assignDivisionHead(id, managerId) {
  return request(`/managers/divisions/${id}/head`, { method: 'PUT', body: { manager_id: managerId } });
}

export function listDivisionHeadCandidates(id, { q, page, perPage } = {}) {
  const query = {};
  if (q) query.q = q;
  if (page) query.page = page;
  if (perPage && perPage !== 30) query.per_page = perPage;
  return request(`/managers/divisions/${id}/head-candidates`, { query });
}

export function getManager(id, query) {
  return request(`/managers/${id}`, { query });
}

export function getManagerQuarters(id, quarterStart) {
  const query = {};
  if (quarterStart) query.quarter_start = quarterStart;
  return request(`/managers/${id}/quarters`, { query });
}

export function listManagerClients({ id, page, perPage, sort, order, filters } = {}) {
  const query = {};
  if (page) query.page = page;
  if (perPage && perPage !== 30) query.per_page = perPage;
  if (sort) query.sort = sort;
  if (order) query.order = order;
  const path = `/managers/${id}/clients`;
  if (filters && Object.keys(filters).length) {
    return request(path, { method: 'POST', query, body: filters });
  }
  return request(path, { query });
}

export function loginAsClient(clientId, body) {
  if (body) return request(`/clients/${clientId}/login`, { method: 'POST', body });
  return request(`/clients/${clientId}/login`, { method: 'POST' });
}

export function listClientTrustedPersons(clientId) {
  return request(`/clients/${clientId}/trusted-persons`);
}

export function listManagerOrders({ id, page, perPage, sort, order, ozm, legalEntity } = {}) {
  const query = {};
  if (page) query.page = page;
  if (perPage && perPage !== 30) query.per_page = perPage;
  if (sort) query.sort = sort;
  if (order) query.order = order;
  if (ozm && ozm !== 'all') query.ozm = ozm;
  if (legalEntity && legalEntity !== 'all') query.legal_entity = legalEntity;
  return request(`/managers/${id}/orders`, { query });
}

export function exportManagerOrders(id) {
  return requestFile(`/managers/${id}/orders/export`, {
    method: 'POST',
    body: {},
    fallbackName: `manager-${id}-orders.csv`,
  });
}

export function getOrder(orderId) {
  return request(`/orders/${orderId}`);
}

export function applyOrderAction(orderId, action, body) {
  if (body) {
    return request(`/orders/${orderId}/${action}`, { method: 'POST', body });
  }
  return request(`/orders/${orderId}/${action}`, { method: 'POST' });
}

export function moveManagerDivision(id, divisionId) {
  return request(`/managers/${id}/division`, { method: 'PUT', body: { division_id: divisionId } });
}

export function dismissManager(id, body) {
  return request(`/managers/${id}/dismiss`, { method: 'POST', body });
}

export function deleteUser(id) {
  return request(`/users/${id}`, { method: 'DELETE' });
}

export function restoreUser(id, comment) {
  return request(`/users/${id}/restore`, { method: 'POST', body: { comment } });
}

export function resendInvitation(id) {
  return request(`/users/${id}/resend-invitation`, { method: 'POST' });
}

export function resetUserPassword(id) {
  return request(`/users/${id}/reset-password`, { method: 'POST' });
}

export function getManagerAudit(id, { page, perPage, filter, q } = {}) {
  const query = { page: page || 1, sort: 'occurred_at', order: 'desc' };
  if (perPage && perPage !== 30) query.per_page = perPage;
  if (filter && filter !== 'all') query.filter = filter;
  if (q) query.q = q;
  return request(`/managers/${id}/audit-log`, { query });
}

export function addManagerComment(id, body) {
  return request(`/managers/${id}/comments`, { method: 'POST', body });
}

export function uploadManagerCommentFile(id, body) {
  return request(`/managers/${id}/comment-files`, { method: 'POST', body });
}

export function createManagerCommentFileLink(id, fileId) {
  return request(`/managers/${id}/comment-files/${fileId}/link`, { method: 'POST' });
}

export function getUserAudit(id, { page, perPage, category }) {
  const query = { page, sort: 'occurred_at', order: 'desc' };
  if (perPage && perPage !== 30) query.per_page = perPage;
  if (category) {
    return request(`/users/${id}/audit-log`, { method: 'POST', query, body: { category } });
  }
  return request(`/users/${id}/audit-log`, { query });
}
